import { useEffect, useRef, useState, type CSSProperties } from "react";
import { fetchAddressByCep, formatCep, isCompleteCep, normalizeCep } from "../lib/cep";
import { C, font } from "../theme";
import type { AddressFormValue } from "../lib/memberForm";
import { FieldInput } from "./Field";

interface AddressFieldsProps {
  idPrefix: string;
  value: AddressFormValue;
  onChange: (value: AddressFormValue) => void;
  disabled?: boolean;
  errors?: Partial<Record<keyof AddressFormValue, string>>;
}

type CepStatus = "idle" | "loading" | "success" | "not-found" | "error";

const labelStyle: CSSProperties = {
  display: "block",
  fontFamily: font,
  fontSize: 12,
  fontWeight: 600,
  color: C.gray,
  marginBottom: 4,
  marginTop: 12,
};

const errorStyle: CSSProperties = { color: C.red, fontSize: 11.5, marginTop: 4 };
const hintStyle: CSSProperties = { color: C.light, fontSize: 11.5, marginTop: 4 };

function rowStyle(columns: string): CSSProperties {
  return { display: "grid", gridTemplateColumns: columns, gap: 12 };
}

export function AddressFields({ idPrefix, value, onChange, disabled, errors = {} }: AddressFieldsProps) {
  const [cepStatus, setCepStatus] = useState<CepStatus>("idle");
  const valueRef = useRef(value);
  const lastLookupRef = useRef<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  valueRef.current = value;

  useEffect(() => () => abortRef.current?.abort(), []);

  const id = (field: keyof AddressFormValue) => `${idPrefix}-${field.replace(/[A-Z]/g, letter => `-${letter.toLowerCase()}`)}`;

  const update = (field: keyof AddressFormValue, fieldValue: string) => {
    onChange({ ...valueRef.current, [field]: fieldValue });
  };

  const handleZipCodeChange = (raw: string) => {
    const formatted = formatCep(raw);
    onChange({ ...valueRef.current, zipCode: formatted });
    if (normalizeCep(formatted) !== lastLookupRef.current) {
      lastLookupRef.current = null;
      abortRef.current?.abort();
      setCepStatus("idle");
    }
  };

  const lookupCep = async () => {
    if (disabled) return;
    const digits = normalizeCep(valueRef.current.zipCode);
    if (!isCompleteCep(digits)) {
      lastLookupRef.current = null;
      setCepStatus("idle");
      return;
    }
    if (lastLookupRef.current === digits) return;

    lastLookupRef.current = digits;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setCepStatus("loading");

    try {
      const address = await fetchAddressByCep(digits, { signal: controller.signal });
      if (controller.signal.aborted || normalizeCep(valueRef.current.zipCode) !== digits) return;
      if (!address) {
        setCepStatus("not-found");
        return;
      }
      onChange({
        ...valueRef.current,
        zipCode: formatCep(address.zipCode),
        street: address.street,
        neighborhood: address.neighborhood,
        city: address.city,
        state: address.state,
      });
      setCepStatus("success");
    } catch {
      if (controller.signal.aborted) return;
      lastLookupRef.current = null;
      setCepStatus("error");
    }
  };

  return (
    <div>
      <div style={rowStyle("minmax(0,1fr) minmax(0,1fr)")}>
        <div>
          <label htmlFor={id("zipCode")} style={labelStyle}>CEP</label>
          <FieldInput
            id={id("zipCode")}
            value={value.zipCode}
            onChange={event => handleZipCodeChange(event.target.value)}
            onBlur={() => { void lookupCep(); }}
            placeholder="00000-000"
            inputMode="numeric"
            maxLength={9}
            autoComplete="postal-code"
            disabled={disabled}
          />
          {cepStatus === "loading" && <div role="status" style={hintStyle}>Buscando endereço...</div>}
          {cepStatus === "success" && <div role="status" style={hintStyle}>Endereço preenchido pelo CEP.</div>}
          {cepStatus === "not-found" && <div role="status" style={hintStyle}>CEP não encontrado. Preencha o endereço manualmente.</div>}
          {cepStatus === "error" && <div role="alert" style={errorStyle}>Não foi possível consultar o CEP. Preencha manualmente.</div>}
          {errors.zipCode && <div style={errorStyle}>{errors.zipCode}</div>}
        </div>
        <div>
          <label htmlFor={id("number")} style={labelStyle}>Número</label>
          <FieldInput
            id={id("number")}
            value={value.number}
            onChange={event => update("number", event.target.value)}
            maxLength={20}
            disabled={disabled}
          />
        </div>
      </div>

      <label htmlFor={id("street")} style={labelStyle}>Logradouro</label>
      <FieldInput
        id={id("street")}
        value={value.street}
        onChange={event => update("street", event.target.value)}
        maxLength={160}
        autoComplete="address-line1"
        disabled={disabled}
      />

      <div style={rowStyle("minmax(0,1fr) minmax(0,1fr)")}>
        <div>
          <label htmlFor={id("complement")} style={labelStyle}>Complemento</label>
          <FieldInput
            id={id("complement")}
            value={value.complement}
            onChange={event => update("complement", event.target.value)}
            maxLength={80}
            autoComplete="address-line2"
            disabled={disabled}
          />
        </div>
        <div>
          <label htmlFor={id("neighborhood")} style={labelStyle}>Bairro</label>
          <FieldInput
            id={id("neighborhood")}
            value={value.neighborhood}
            onChange={event => update("neighborhood", event.target.value)}
            maxLength={80}
            disabled={disabled}
          />
        </div>
      </div>

      <div style={rowStyle("minmax(0,1fr) 96px")}>
        <div>
          <label htmlFor={id("city")} style={labelStyle}>Cidade</label>
          <FieldInput
            id={id("city")}
            value={value.city}
            onChange={event => update("city", event.target.value)}
            maxLength={80}
            autoComplete="address-level2"
            disabled={disabled}
          />
        </div>
        <div>
          <label htmlFor={id("state")} style={labelStyle}>UF</label>
          <FieldInput
            id={id("state")}
            value={value.state}
            onChange={event => update("state", event.target.value.toUpperCase())}
            maxLength={2}
            autoComplete="address-level1"
            disabled={disabled}
          />
          {errors.state && <div style={errorStyle}>{errors.state}</div>}
        </div>
      </div>
    </div>
  );
}
